import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { GtpWalletInfoResult } from '@eika-infrastruktur/mobilbank-lib';
import { ErrorMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';

import {
    useGoogleWalletInformation,
    useCheckIfGoogleWalletAvailable,
    useFindDigitalWalletCardQuery,
} from '@/domain/digital-wallet';
import { useCardByTypeAndId } from '@/react-query/hooks/query/use-card';

import { useSession } from '@components/app/Session';
import { CouldNotFetchCardsErrorCard } from '@components/error/ErrorCard';
import { isOwnerOfCard } from '@domain/card/service';
import { Card, CardType } from '@generated-types';
import useText from '@hooks/use-text';
import { useUserIsHighFraudRisk } from '@hooks/use-user-is-high-fraud-risk';

import { GetGoingContent } from './GetGoingContent';
import { GetGoingButtonSkeleton } from './GetGoingSkeleton';

import { GetGoingV4Query } from './GetGoingV4Query';

type DigitalWalletQueryProps = {
    card: Card;
    googleWalletInfo: GtpWalletInfoResult;
};

const DigitalWalletQuery = ({ card, googleWalletInfo }: DigitalWalletQueryProps) => {
    const digitalCardQuery = useFindDigitalWalletCardQuery(card, googleWalletInfo);
    const { googleWalletNotAvailable } = useText();

    if (digitalCardQuery.isPending) {
        return <GetGoingButtonSkeleton />;
    }

    if (digitalCardQuery.isError || digitalCardQuery.data === undefined) {
        return <ErrorMessage text={googleWalletNotAvailable} />;
    }

    return <GetGoingContent data={{ card, digitalCard: digitalCardQuery.data, source: 'te' }} />;
};

const GetGoingCardAndWalletQuery = ({ cardId, cardType }: { cardId: string; cardType: string }) => {
    const session = useSession();

    const cardQuery = useCardByTypeAndId(cardType as CardType, cardId);

    const googleWalletInfoQuery = useGoogleWalletInformation();
    const { googleWalletNotCardOwnerError, googleWalletGetGoingFetchError } = useText();

    if (cardQuery.isPending || googleWalletInfoQuery.isPending) {
        return <GetGoingButtonSkeleton />;
    }

    if (cardQuery.isError) {
        return <CouldNotFetchCardsErrorCard tryAgainClick={cardQuery.refetch} />;
    }

    if (googleWalletInfoQuery.isError) {
        return <ErrorMessage text={googleWalletGetGoingFetchError} icon={IconTypes.LUKK_24} />;
    }

    if (googleWalletInfoQuery.data.status === 'error') {
        return <ErrorMessage text={googleWalletInfoQuery.data.error} icon={IconTypes.LUKK_24} />;
    }

    const card = cardQuery.data;
    const walletInfo = googleWalletInfoQuery.data.data;

    if (!isOwnerOfCard(card, session)) {
        return <ErrorMessage text={googleWalletNotCardOwnerError} />;
    }

    return <DigitalWalletQuery card={card} googleWalletInfo={walletInfo} />;
};

export const GetGoing = () => {
    const userIsHighFraudRisk = useUserIsHighFraudRisk();
    const isGoogleWalletAvailable = useCheckIfGoogleWalletAvailable();
    const { googleWalletTemporaryUnavailable, googleWalletNotAvailable } = useText();
    const [searchParams] = useSearchParams();

    if (!isGoogleWalletAvailable) {
        return <ErrorMessage text={googleWalletNotAvailable} />;
    }

    if (userIsHighFraudRisk) {
        return <ErrorMessage text={googleWalletTemporaryUnavailable} />;
    }

    const cardType = searchParams.get('ctype');

    if (cardType === CardType.CREDIT) {
        return <GetGoingV4Query />;
    }

    const cardId = searchParams.get('cid');

    if (cardId === null || cardType === null) {
        return <ErrorMessage text={googleWalletNotAvailable} />;
    }

    return <GetGoingCardAndWalletQuery cardType={cardType} cardId={cardId} />;
};
